import { useEffect, useState } from "react";
import { Button, FormField } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { DECLINE_REASONS } from "@/api/dealLookups";
import type { DealListItemDto, DealDetailDto } from "@/types/deals";
import { useDeclineDeal } from "@/hooks/deals/useDeals";
import { dealLabel } from "./dealFormat";

interface Props {
  open: boolean;
  deal: DealListItemDto | DealDetailDto | null;
  onClose: () => void;
  onDeclined?: (deal: DealDetailDto) => void;
}

const MIN_DETAIL = 20;
/** The server stores the detail in 2000-character columns and refuses a longer
 *  one (owner approval, 2026-10-13). */
const MAX_DETAIL = 2000;

/** Spec §5.7 — Decline modal. Reason code (DeclineReasonRef) and required
 *  detail (≥20 chars). The intro says only what the decline does: the deal's
 *  C1 and B1 are notified in-app (DealActivityNotificationInterceptor); no
 *  email reaches the broker and nothing archives the deal on its own. */
export function DeclineModal({ open, deal, onClose, onDeclined }: Props) {
  const toast = useToast();
  const decline = useDeclineDeal(deal?.id ?? "");
  const [reasonCode, setReasonCode] = useState("");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReasonCode("");
      setDetail("");
      setError(null);
    }
  }, [open]);

  if (!deal) return null;

  async function submit() {
    if (!reasonCode) {
      setError("Select a decline reason.");
      return;
    }
    if (detail.trim().length < MIN_DETAIL) {
      setError(`Detail must be at least ${MIN_DETAIL} characters.`);
      return;
    }
    if (detail.trim().length > MAX_DETAIL) {
      setError(`Detail must be at most ${MAX_DETAIL} characters.`);
      return;
    }
    setError(null);
    try {
      const result = await decline.mutateAsync({
        reasonCode,
        detail: detail.trim(),
      });
      toast.success(`Declined ${dealLabel(deal!)}.`);
      onDeclined?.(result);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : "Decline failed.";
      setError(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Decline deal"
      description={`${deal.dealNumber} — ${dealLabel(deal)}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={decline.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} loading={decline.isPending}>
            Decline deal
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-gray-mid">
          The deal will move to <strong>Declined</strong>, and its C1 and B1 are
          notified in Phoenix. The broker is not notified. The deal then becomes
          read-only: only an admin can edit it, though it can still be cloned or
          archived from its header menu.
        </p>

        <FormField label="Reason" required>
          <Select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            aria-label="Decline reason"
          >
            <option value="">Select a reason…</option>
            {DECLINE_REASONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.displayName}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Justification" required helperText={`${MIN_DETAIL}–${MAX_DETAIL} characters.`}>
          <Textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            maxLength={MAX_DETAIL}
            rows={5}
            placeholder="Explain the decline so audit + future broker conversations have context."
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
